

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { SampleData } from '@/services/spreadsheetService';
import SpreadsheetForm from './SpreadsheetForm';
import FileUploadMapping from './FileUploadMapping';
import MappingTable, { MappingConfiguration } from './MappingTable';
import { submitMappingConfiguration } from '@/services/spreadsheetService';

interface MappingInterfaceProps {
  sampleData: SampleData | null;
  onDataFetched: (data: SampleData) => void;
  isDataSectionCollapsed: boolean;
  vendorName?: string;
}

const MappingInterface: React.FC<MappingInterfaceProps> = ({ 
  sampleData, 
  onDataFetched, 
  isDataSectionCollapsed,
  vendorName 
}) => {
  const [supplierHeaders, setSupplierHeaders] = useState<string[]>([]);
  const [showMappingTable, setShowMappingTable] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDataFetched = (data: SampleData) => {
    onDataFetched(data);
    setSupplierHeaders(Object.keys(data.firstRowItems));
    setShowMappingTable(true);
  };

  const handleFileProcessed = (data: { headers: string[]; sampleData?: any }) => {
    setSupplierHeaders(data.headers);
    setShowMappingTable(true);
    toast.success('File processed successfully. You can now configure mappings.');
  };

  const handleMappingSubmit = async (mappings: MappingConfiguration[]) => {
    setIsSubmitting(true);
    try {
      await submitMappingConfiguration(mappings);
      toast.success('Mapping configuration submitted successfully!');
      console.log('Submitted mappings:', mappings);
    } catch (error) {
      console.error('Submission failed:', error);
      toast.error('Failed to submit mapping configuration');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`transition-all duration-300 ${isDataSectionCollapsed ? 'lg:flex-1' : 'lg:w-2/5'}`}>
      <Card className="h-[calc(100vh-200px)]">
        <CardHeader>
          <CardTitle>Create Mapping Configuration</CardTitle>
          <CardDescription>Upload a file or connect to Google Sheets, then configure attribute mappings</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[calc(100vh-280px)] pr-4">
            {/* File Upload Section */}
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-sm mb-3">Upload File</h3>
                <FileUploadMapping onFileProcessed={handleFileProcessed} />
              </div>

              <div className="flex items-center gap-4">
                <Separator className="flex-1" />
                <span className="text-xs text-muted-foreground">OR</span>
                <Separator className="flex-1" />
              </div>

              {/* Google Sheets Section */}
              <div>
                <h3 className="font-medium text-sm mb-3">Connect to Google Sheets</h3>
                <SpreadsheetForm onDataFetched={handleDataFetched} />
              </div>

              {/* Mapping Table */}
              {showMappingTable && supplierHeaders.length > 0 && (
                <div className="mt-6">
                  <Separator className="mb-4" />
                  <h3 className="font-medium text-lg mb-4">Configure Attribute Mappings</h3>
                  <MappingTable 
                    supplierHeaders={supplierHeaders}
                    onSubmit={handleMappingSubmit}
                    vendorName={vendorName}
                  />
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default MappingInterface;